import { useState } from 'react';

import { Book, RefreshCw } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { SettingsWrapper } from './SettingsWrapper';
import { Button } from '@/components/atoms/new-button';
import PermittedFilesChip from '@/components/molecules/PermittedFilesChip';
import FileUploaderComponent from '@/components/organisms/DocumentAdder';
import { useJobProgress } from '@/hooks/useJobProgress';

interface DocumentsProps {
  openModal: (open: boolean) => void;
  setTotalUnprocessedDocs: (docNumber: number) => void;
  detailsChanged: boolean;
  isReprocessing: boolean;
}

export const Documents = ({ openModal, setTotalUnprocessedDocs, detailsChanged, isReprocessing }: DocumentsProps) => {
  const [uploadDisabled, setUploadDisabled] = useState(false);
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');
  const navigate = useNavigate();

  // Track active jobs for zip extraction
  const { activeJobs } = useJobProgress(caseId || '');

  return (
    <SettingsWrapper>
      <div>
        <div>
          <div className="mb-2 flex items-center justify-between gap-4">
            <h2 className="text-base font-semibold text-gray-900">Add Documents</h2>
            <PermittedFilesChip />
          </div>
          <p className="mb-4 text-sm text-gray-500">
            Upload additional documents to your case. Use the buttons below to navigate to case details or update your
            case.
          </p>
        </div>
        <FileUploaderComponent
          setUploadDisabled={setUploadDisabled}
          setTotalUnprocessedDocs={setTotalUnprocessedDocs}
          caseId={caseId || ''}
          activeJobs={activeJobs}
        />
      </div>
      <div className="mt-6 border-t border-gray-200 pt-4 pr-4">
        <div className="flex gap-3">
          <Button
            variant="brand"
            size="sm"
            disabled={(uploadDisabled && !detailsChanged) || isReprocessing || activeJobs.length > 0}
            onClick={() => openModal(true)}
          >
            <RefreshCw className="h-4 w-4" />
            Update Case
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/app/chronos/case-editor/settings/details?caseId=${caseId}`)}
            disabled={isReprocessing}
          >
            <Book className="h-4 w-4" />
            Case Details
          </Button>
        </div>
        {isReprocessing ? (
          <p className="mt-2 text-xs text-amber-600">
            Case is currently being processed. Updates are temporarily disabled.
          </p>
        ) : (
          <p className="mt-2 text-xs text-gray-500">
            Update just with new documents, or change case details as well, then update everything together.
          </p>
        )}
      </div>
    </SettingsWrapper>
  );
};
